import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Search } from "lucide-react";
import { api } from "@/lib/api";
import { EPISODES } from "@/components/Derive";
import { CHANNEL_META } from "@/components/RadioChannels";
import { RadioBirthAdmin } from "@/components/RadioBirthAdmin";
import { DubTrialAdmin } from "@/components/DubTrialAdmin";

const IDS = ["silence", "liberte", "doute", "nature"];

export function RadioChannelsAdmin() {
  const [map, setMap] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    fetch("/derive/radio/channels.json", { cache: "no-store" }).then((r) => r.json())
      .then((stat) => api.get("/radio/channels").then(({ data }) => data.channels || stat).catch(() => stat))
      .then((ch) => setMap(Object.fromEntries(IDS.map((id) => [id, new Set(ch[id] || [])]))))
      .catch(() => setMap(Object.fromEntries(IDS.map((id) => [id, new Set()])))); 
  }, []);

  const toggle = (id, slug) => {
    setMap((m) => {
      const s = new Set(m[id]);
      s.has(slug) ? s.delete(slug) : s.add(slug);
      return { ...m, [id]: s };
    });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    const order = EPISODES.map((e) => e.slug);
    const channels = Object.fromEntries(IDS.map((id) => [id, order.filter((s) => map[id].has(s))]));
    try {
      const { data } = await api.put("/admin/radio/channels", { channels });
      toast.success(`Chaînes enregistrées : ${IDS.map((id) => `${CHANNEL_META[id].label.fr} ${data.counts[id]}`).join(" · ")}`);
      setDirty(false);
    } catch { toast.error("Enregistrement impossible"); } finally { setSaving(false); }
  };

  const list = useMemo(() => EPISODES.map((e, i) => ({ ...e, num: EPISODES.length - i }))
    .filter((e) => !q || e.title.fr.toLowerCase().includes(q.toLowerCase())), [q]);

  if (!map) return <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-primary" /></div>;

  return (
    <div data-testid="admin-radio-channels">
      <DubTrialAdmin />
      <RadioBirthAdmin />
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un épisode…" data-testid="admin-radio-search"
            className="w-full rounded-xl bg-background/60 border border-white/10 pl-9 pr-4 py-2.5 text-sm focus:border-primary/50 focus:outline-none" />
        </div>
        <div className="flex gap-2 text-xs text-foreground/60">
          {IDS.map((id) => <span key={id} data-testid={`admin-radio-count-${id}`}>{CHANNEL_META[id].label.fr} <b className="text-foreground/90">{map[id].size}</b></span>)}
        </div>
        <button onClick={save} disabled={!dirty || saving} data-testid="admin-radio-save"
          className="inline-flex items-center gap-2 rounded-full bg-primary text-background font-semibold px-5 py-2.5 text-sm disabled:opacity-40">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Enregistrer
        </button>
      </div>
      <div className="space-y-1.5">
        {list.map((e) => (
          <div key={e.slug} className="flex flex-wrap items-center gap-3 rounded-xl bg-white/[0.03] border border-white/5 px-3 py-2" data-testid={`admin-radio-row-${e.slug}`}>
            <span className="w-9 text-xs text-foreground/40 tabular-nums">N°{e.num}</span>
            <span className="flex-1 min-w-[180px] text-sm truncate">{e.title.fr}</span>
            <div className="flex gap-1.5">
              {IDS.map((id) => {
                const on = map[id].has(e.slug);
                const Icon = CHANNEL_META[id].icon;
                return (
                  <button key={id} onClick={() => toggle(id, e.slug)} data-testid={`admin-radio-toggle-${e.slug}-${id}`} aria-pressed={on}
                    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[0.7rem] border transition-colors ${on ? "bg-primary/20 border-primary/60 text-primary" : "border-white/10 text-foreground/40 hover:text-foreground/70"}`}>
                    <Icon size={11} /> {CHANNEL_META[id].label.fr}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
